import React, { useEffect, useState } from "react";
import { adminApi } from "@/lib/api";
import { Download, X, ArrowRight, MousePointerClick, ShoppingCart } from "lucide-react";

const fmtDate = (s) => (s ? new Date(s).toLocaleString() : "—");

export default function Visitors() {
  const [data, setData] = useState({ visitors: [], total: 0, page: 1, pages: 1 });
  const [page, setPage] = useState(1);
  const [journey, setJourney] = useState(null);
  const [loadingJ, setLoadingJ] = useState(false);

  useEffect(() => {
    adminApi.get(`/admin/analytics/visitors?page=${page}&limit=50`).then((r) => setData(r.data)).catch(() => {});
  }, [page]);

  const openJourney = async (sid) => {
    setLoadingJ(true);
    setJourney({ session_id: sid });
    try {
      const { data } = await adminApi.get(`/admin/analytics/journey/${sid}`);
      setJourney(data);
    } finally {
      setLoadingJ(false);
    }
  };

  const exportCsv = () => {
    const rows = [["Date/Time", "IP", "Country", "City", "Region", "Referrer", "Device", "Browser", "OS", "Landing", "Pageviews", "Duration"]];
    data.visitors.forEach((v) => rows.push([fmtDate(v.first_seen), v.ip, v.country, v.city, v.region, v.referrer || "Direct", v.device, v.browser, v.os, v.landing_page, v.pageviews, v.duration_label]));
    const csv = rows.map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    a.download = `equine-evolve-visitors-p${page}.csv`;
    a.click();
  };

  return (
    <div data-testid="admin-visitors">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <p className="ee-overline mb-2">Insights</p>
          <h1 className="font-display text-4xl tracking-tight">Visitors</h1>
          <p className="text-[#A8A29E] text-sm mt-2">{data.total} sessions tracked</p>
        </div>
        <button onClick={exportCsv} data-testid="visitors-export-csv" className="inline-flex items-center gap-2 border border-[#D4AF37] text-[#D4AF37] px-5 py-3 uppercase tracking-[0.15em] text-xs hover:bg-[#D4AF37] hover:text-[#0C0A09] transition-colors">
          <Download size={14} /> Export CSV
        </button>
      </div>

      <div className="border ee-hairline bg-[#1C1917] overflow-x-auto">
        <table className="w-full text-sm min-w-[820px]">
          <thead>
            <tr className="text-left text-[#57534E] uppercase text-[10px] tracking-[0.15em] border-b ee-hairline">
              {["Date / Time", "IP", "Location", "Referrer", "Device", "Views", "Time", ""].map((h) => <th key={h} className="p-3 font-normal">{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {data.visitors.map((v) => (
              <tr key={v.session_id} className="border-b border-white/5 hover:bg-white/5">
                <td className="p-3 whitespace-nowrap text-[#A8A29E]">{fmtDate(v.first_seen)}</td>
                <td className="p-3 text-[#FAFAF9]">{v.ip || "—"}</td>
                <td className="p-3 text-[#A8A29E]">{[v.city, v.region, v.country].filter(Boolean).join(", ") || "Unknown"}</td>
                <td className="p-3 text-[#A8A29E] max-w-[180px] truncate" title={v.referrer || "Direct"}>{v.referrer || "Direct"}</td>
                <td className="p-3 text-[#A8A29E]">{v.device} · {v.browser}</td>
                <td className="p-3 text-[#A8A29E]">{v.pageviews}</td>
                <td className="p-3 text-[#A8A29E]">{v.duration_label}</td>
                <td className="p-3"><button onClick={() => openJourney(v.session_id)} data-testid={`journey-btn-${v.session_id}`} className="ee-gold-text inline-flex items-center gap-1 hover:underline whitespace-nowrap">Journey <ArrowRight size={13} /></button></td>
              </tr>
            ))}
            {data.visitors.length === 0 && <tr><td colSpan={8} className="p-8 text-center text-[#57534E]">No visits recorded yet.</td></tr>}
          </tbody>
        </table>
      </div>

      <div className="flex justify-between items-center mt-5 text-sm text-[#A8A29E]">
        <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="disabled:opacity-40 hover:text-[#D4AF37]">← Prev</button>
        <span>Page {data.page} / {data.pages}</span>
        <button disabled={page >= data.pages} onClick={() => setPage((p) => p + 1)} className="disabled:opacity-40 hover:text-[#D4AF37]">Next →</button>
      </div>

      {journey && (
        <div className="fixed inset-0 z-50 flex justify-end" data-testid="journey-panel">
          <div className="absolute inset-0 bg-black/70" onClick={() => setJourney(null)} />
          <div className="relative w-full max-w-md bg-[#1C1917] border-l ee-hairline h-full overflow-y-auto p-7">
            <button onClick={() => setJourney(null)} data-testid="journey-close" className="absolute top-4 right-4 text-[#A8A29E] hover:text-[#D4AF37]"><X size={20} /></button>
            <p className="ee-overline mb-1">Visitor journey</p>
            <h2 className="font-display text-2xl mb-1">{journey.session?.city || "Unknown"}{journey.session?.country ? `, ${journey.session.country}` : ""}</h2>
            <p className="text-xs text-[#57534E] mb-5">{journey.session?.ip} · {journey.session?.device} · {journey.session?.browser}</p>
            {loadingJ ? <p className="ee-overline animate-pulse">Loading…</p> : (
              <>
                <div className="flex gap-2 mb-5 flex-wrap">
                  {journey.reached_cart && <span className="inline-flex items-center gap-1 text-xs border border-[#D4AF37]/40 text-[#D4AF37] px-2 py-1"><ShoppingCart size={12} /> Added to cart</span>}
                  {journey.reached_checkout && <span className="inline-flex items-center gap-1 text-xs border border-emerald-500/40 text-emerald-400 px-2 py-1"><MousePointerClick size={12} /> Checkout</span>}
                </div>
                {journey.products_viewed?.length > 0 && (
                  <div className="mb-6">
                    <p className="ee-overline mb-2">Products viewed ({journey.products_viewed.length})</p>
                    {journey.products_viewed.map((p) => <p key={p.slug} className="text-sm text-[#FAFAF9]">• {p.title || p.slug}</p>)}
                  </div>
                )}
                <p className="ee-overline mb-3">Timeline</p>
                <ol className="relative border-l ee-hairline pl-5 space-y-4">
                  {journey.timeline?.map((t, i) => (
                    <li key={i} className="relative">
                      <span className="absolute -left-[23px] top-1 h-2.5 w-2.5 rounded-full bg-[#D4AF37]" />
                      {t.kind === "pageview" ? (
                        <><p className="text-sm text-[#FAFAF9] break-all">{t.path}</p><p className="text-xs text-[#57534E]">{fmtDate(t.at)} · {t.duration_label}</p></>
                      ) : (
                        <><p className="text-sm text-[#D4AF37]">⚑ {t.type}{t.product_slug ? ` · ${t.product_slug}` : ""}</p><p className="text-xs text-[#57534E]">{fmtDate(t.at)}</p></>
                      )}
                    </li>
                  ))}
                </ol>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
